// WebSocket message types
export enum WebSocketMessageType {
  ROOMS_UPDATE = 'ROOMS_UPDATE',
}

// WebSocket message interface
export interface WebSocketMessage {
  type: WebSocketMessageType;
  data: any;
}

// WebSocket event listeners
type MessageListener = (data: any) => void;
type ConnectionListener = () => void;
type ErrorListener = (error: Event) => void;

// WebSocket service class
class WebSocketService {
  private socket: WebSocket | null = null;
  private reconnectTimeout: number = 1000; // Start with 1s timeout
  private maxReconnectTimeout: number = 30000; // Max 30s timeout
  private messageListeners: Map<WebSocketMessageType, Set<MessageListener>> = new Map();
  private connectListeners: Set<ConnectionListener> = new Set();
  private disconnectListeners: Set<ConnectionListener> = new Set();
  private errorListeners: Set<ErrorListener> = new Set();
  private isConnecting: boolean = false;

  // Connect to the WebSocket server
  public connect(): void {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return; // Already connected or connecting
    }

    if (this.isConnecting) {
      return; // Already attempting to connect
    }

    this.isConnecting = true;

    // Determine WebSocket URL based on current location
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const wsUrl = `${protocol}//${host}`;

    try {
      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = () => {
        console.log('WebSocket connected');
        this.reconnectTimeout = 1000; // Reset reconnect timeout on successful connection
        this.isConnecting = false;
        this.notifyConnectListeners();
      };

      this.socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as WebSocketMessage;
          this.handleMessage(message);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.socket.onclose = () => {
        console.log('WebSocket disconnected, reconnecting...');
        this.isConnecting = false;
        this.notifyDisconnectListeners();
        this.scheduleReconnect();
      };

      this.socket.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.isConnecting = false;
        this.notifyErrorListeners(error);
        // Don't reconnect here, let onclose handle it
      };
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  // Schedule reconnection with exponential backoff
  private scheduleReconnect(): void {
    setTimeout(() => {
      this.connect();
    }, this.reconnectTimeout);

    // Increase reconnect timeout for next attempt (exponential backoff)
    this.reconnectTimeout = Math.min(this.reconnectTimeout * 1.5, this.maxReconnectTimeout);
  }

  // Handle incoming WebSocket messages
  private handleMessage(message: WebSocketMessage): void {
    const listeners = this.messageListeners.get(message.type);
    if (listeners) {
      listeners.forEach(listener => listener(message.data));
    }
  }

  // Add a message listener for a specific message type
  public addMessageListener(type: WebSocketMessageType, listener: MessageListener): void {
    if (!this.messageListeners.has(type)) {
      this.messageListeners.set(type, new Set());
    }
    this.messageListeners.get(type)!.add(listener);
  }

  // Remove a message listener
  public removeMessageListener(type: WebSocketMessageType, listener: MessageListener): void {
    const listeners = this.messageListeners.get(type);
    if (listeners) {
      listeners.delete(listener);
    }
  }

  // Add a connection listener
  public addConnectListener(listener: ConnectionListener): void {
    this.connectListeners.add(listener);
  }

  // Remove a connection listener
  public removeConnectListener(listener: ConnectionListener): void {
    this.connectListeners.delete(listener);
  }

  // Add a disconnection listener
  public addDisconnectListener(listener: ConnectionListener): void {
    this.disconnectListeners.add(listener);
  }

  // Remove a disconnection listener
  public removeDisconnectListener(listener: ConnectionListener): void {
    this.disconnectListeners.delete(listener);
  }

  // Add an error listener
  public addErrorListener(listener: ErrorListener): void {
    this.errorListeners.add(listener);
  }

  // Remove an error listener
  public removeErrorListener(listener: ErrorListener): void {
    this.errorListeners.delete(listener);
  }

  // Notify all connect listeners
  private notifyConnectListeners(): void {
    this.connectListeners.forEach(listener => listener());
  }

  // Notify all disconnect listeners
  private notifyDisconnectListeners(): void {
    this.disconnectListeners.forEach(listener => listener());
  }

  // Notify all error listeners
  private notifyErrorListeners(error: Event): void {
    this.errorListeners.forEach(listener => listener(error));
  }

  // Close the WebSocket connection
  public disconnect(): void {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }
}

// Create a singleton instance
const websocketService = new WebSocketService();

export default websocketService;
